import type { ItemChecklist, PortalEstado, TipoDocumento } from '@contefy/shared';
import { AlertTriangle, Camera, CheckCircle2, Clock, FileUp, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Alerta, Botao, Cartao, Selo, Titulo } from './ui';

export type ResultadoEnvio = { status: string; motivo: string | null; orientacao: string | null };

export function Documentos({
  estado,
  aoEnviar,
  aoRemover,
}: {
  estado: PortalEstado;
  aoEnviar: (tipo: TipoDocumento, socioId: string | null, arquivo: File) => Promise<ResultadoEnvio>;
  aoRemover: (documentoId: string) => Promise<void>;
}) {
  const porSocio = new Map<string, ItemChecklist[]>();
  for (const item of estado.checklist) {
    const chave = item.socio_nome ?? 'A empresa';
    porSocio.set(chave, [...(porSocio.get(chave) ?? []), item]);
  }
  const faltam = estado.checklist.filter(
    (i) => i.obrigatorio && ['pendente', 'ilegivel', 'rejeitado'].includes(i.status),
  ).length;

  // Por padrão a página é só acompanhamento: mostra o que veio com o formulário, sem envio.
  if (!estado.envio_habilitado) {
    const recebidos = estado.checklist.filter((i) => i.documento_ids.length > 0);
    return (
      <Cartao>
        <Titulo sub="Documentos que vieram com o seu formulário de abertura. Se precisarmos de algo mais, nossa equipe entra em contato.">
          Documentos recebidos
        </Titulo>
        {recebidos.length === 0 ? (
          <p className="text-sm text-slate-600">Nenhum documento anexado ao formulário.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recebidos.map((item) => (
              <li key={item.chave} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{item.titulo}</p>
                  {item.socio_nome && <p className="text-sm text-slate-600">{item.socio_nome}</p>}
                </div>
                <StatusSelo status={item.status} />
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    );
  }

  return (
    <Cartao>
      <Titulo
        sub={
          faltam === 0
            ? 'Recebemos todos os documentos obrigatórios.'
            : `Faltam ${faltam} ${faltam === 1 ? 'item' : 'itens'}. Pode ser foto do celular ou PDF.`
        }
      >
        Documentos
      </Titulo>
      <div className="space-y-6">
        {[...porSocio.entries()].map(([grupo, itens]) => (
          <div key={grupo}>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
              {grupo}
            </h3>
            <ul className="space-y-3">
              {itens.map((item) => (
                <ItemDocumento
                  key={item.chave}
                  item={item}
                  aoEnviar={(f) => aoEnviar(item.tipo, item.socio_id, f)}
                  aoRemover={aoRemover}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Cartao>
  );
}

function ItemDocumento({
  item,
  aoEnviar,
  aoRemover,
}: {
  item: ItemChecklist;
  aoEnviar: (arquivo: File) => Promise<ResultadoEnvio>;
  aoRemover: (documentoId: string) => Promise<void>;
}) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoEnvio | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  const escolher = async (lista: FileList | null) => {
    const arquivo = lista?.[0];
    if (!arquivo) return;
    setEnviando(true);
    setErro(null);
    setResultado(null);
    try {
      const r = await aoEnviar(arquivo);
      setResultado(r);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (arquivoRef.current) arquivoRef.current.value = '';
    }
  };

  const ok = ['enviado', 'extraido', 'aceito'].includes(item.status);
  const problema = item.status === 'ilegivel' || item.status === 'rejeitado';

  return (
    <li
      className={`rounded-xl border p-3 ${ok ? 'border-emerald-200 bg-emerald-50/40' : problema ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-slate-900">
            {item.titulo}
            {!item.obrigatorio && (
              <span className="ml-2 text-xs font-normal text-slate-500">(opcional)</span>
            )}
          </p>
          <p className="mt-0.5 text-sm text-slate-600">{item.instrucao}</p>
          {item.exemplos && <p className="mt-0.5 text-xs text-slate-500">{item.exemplos}</p>}
        </div>
        <StatusSelo status={item.status} />
      </div>

      {problema && (item.motivo || item.orientacao) && (
        <div className="mt-3">
          <Alerta tipo="aviso">
            <span className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {item.motivo} {item.orientacao}
              </span>
            </span>
          </Alerta>
        </div>
      )}

      {resultado && resultado.status !== 'ilegivel' && (
        <div className="mt-3">
          <Alerta tipo="sucesso">
            {resultado.motivo ?? 'Recebido! Vamos conferir e avisamos se precisar reenviar.'}
          </Alerta>
        </div>
      )}
      {erro && (
        <div className="mt-3">
          <Alerta tipo="erro">{erro}</Alerta>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => escolher(e.target.files)}
        />
        <input
          ref={arquivoRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => escolher(e.target.files)}
        />
        <Botao
          variante={ok ? 'secundario' : 'primario'}
          carregando={enviando}
          onClick={() => cameraRef.current?.click()}
        >
          <Camera className="h-4 w-4" /> {ok ? 'Enviar outra foto' : 'Tirar foto'}
        </Botao>
        <Botao
          variante="secundario"
          disabled={enviando}
          onClick={() => arquivoRef.current?.click()}
        >
          <FileUp className="h-4 w-4" /> Escolher arquivo
        </Botao>
        {item.documento_ids.length > 0 && item.status !== 'aceito' && (
          <Botao
            variante="fantasma"
            className="ml-auto text-sm"
            disabled={enviando}
            onClick={async () => {
              if (!confirm('Remover o arquivo enviado?')) return;
              try {
                await aoRemover(item.documento_ids[0]!);
                setResultado(null);
              } catch (e) {
                setErro((e as Error).message);
              }
            }}
          >
            <Trash2 className="h-4 w-4" /> Remover
          </Botao>
        )}
      </div>
    </li>
  );
}

function StatusSelo({ status }: { status: ItemChecklist['status'] }) {
  switch (status) {
    case 'aceito':
      return (
        <Selo cor="verde">
          <CheckCircle2 className="h-3.5 w-3.5" /> Conferido
        </Selo>
      );
    case 'extraido':
    case 'enviado':
      return (
        <Selo cor="verde">
          <CheckCircle2 className="h-3.5 w-3.5" /> Recebido
        </Selo>
      );
    case 'ilegivel':
      return (
        <Selo cor="amarelo">
          <AlertTriangle className="h-3.5 w-3.5" /> Reenviar
        </Selo>
      );
    case 'rejeitado':
      return (
        <Selo cor="vermelho">
          <AlertTriangle className="h-3.5 w-3.5" /> Reenviar
        </Selo>
      );
    default:
      return (
        <Selo cor="cinza">
          <Clock className="h-3.5 w-3.5" /> Falta
        </Selo>
      );
  }
}
